import type { MatchInvitation, MatchDetails } from "@/components/matchmaking/types";
import type { Match, MatchType } from "@/types/match";

/**
 * Transform invitation to match data
 */
export function invitationToMatchData(invitation: MatchInvitation): Partial<Match> {
  const now = new Date();
  
  return {
    participants: [invitation.challengerId, invitation.challengedId],
    courseId: invitation.matchDetails.courseId,
    courseName: invitation.matchDetails.courseName,
    course: invitation.matchDetails.courseName || "",
    location: invitation.matchDetails.courseName || "",
    type: (invitation.matchDetails.matchFormat as unknown as MatchType) || "competitive",
    format: "scheduled",
    status: "upcoming",
    date: now,
    createdAt: now,
    golferId: invitation.challengerId,
    verified: false,
    pendingVerification: false,
  };
}

/**
 * Calculate match deadline based on rules
 * Standard: 48 hours after match completion to submit scorecard
 */
export function calculateScorecardDeadline(matchDate: Date, matchType: MatchType): Date {
  const deadline = new Date(matchDate);
  
  // Add different deadlines based on match type
  switch (matchType) {
    case "tournament":
      deadline.setHours(deadline.getHours() + 24); // 24 hours for tournaments
      break;
    case "league":
      deadline.setHours(deadline.getHours() + 36); // 36 hours for league matches
      break;
    case "competitive":
      deadline.setHours(deadline.getHours() + 48); // 48 hours for competitive
      break;
    default:
      deadline.setHours(deadline.getHours() + 72); // 72 hours for casual matches
  }
  
  return deadline;
}

/**
 * Determine if scorecard photo is required
 */
export function isPhotocardRequired(matchType: MatchType, leagueId?: string): boolean {
  // Require photos for competitive matches and all league matches
  if (matchType === "competitive" || matchType === "tournament") {
    return true;
  }
  
  // Require photos for league matches
  if (leagueId && matchType === "league") {
    return true;
  }
  
  return false;
}

/**
 * Calculate MMR changes for match results using ELO-style system
 */
export function calculateMatchMMR(
  playerScore: number,
  opponentScore: number,
  playerMMR: number,
  opponentMMR: number,
  matchType: MatchType = "competitive"
): { playerChange: number; opponentChange: number } {
  // Base K-factor (rate of change)
  const K_FACTOR = 32;
  
  // Match type multipliers
  const typeMultipliers = {
    casual: 0.25,
    competitive: 0.75,
    club: 1.0,
    league: 1.0,
    tournament: 1.5,
  };
  
  const multiplier = typeMultipliers[matchType] || 1.0;
  
  // Calculate expected scores using ELO formula
  const expectedPlayerScore = 1 / (1 + Math.pow(10, (opponentMMR - playerMMR) / 400));
  const expectedOpponentScore = 1 / (1 + Math.pow(10, (playerMMR - opponentMMR) / 400));
  
  // Determine actual scores (1 for win, 0.5 for tie, 0 for loss)
  let actualPlayerScore: number;
  let actualOpponentScore: number;
  
  if (playerScore < opponentScore) {
    actualPlayerScore = 1; // Player won (lower score wins in golf)
    actualOpponentScore = 0;
  } else if (playerScore > opponentScore) {
    actualPlayerScore = 0; // Player lost
    actualOpponentScore = 1;
  } else {
    actualPlayerScore = 0.5; // Tie
    actualOpponentScore = 0.5;
  }
  
  // Calculate MMR changes
  const playerChange = Math.round(K_FACTOR * multiplier * (actualPlayerScore - expectedPlayerScore));
  const opponentChange = Math.round(K_FACTOR * multiplier * (actualOpponentScore - expectedOpponentScore));
  
  return {
    playerChange,
    opponentChange,
  };
}

/**
 * Generate a unique match ID
 */
export function generateMatchId(): string {
  return `match_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
